import React, { useEffect, useRef, useState } from "react";
import { ConfirmButton } from "../../components/ConfirmButton";
import { notification } from "../../helpers/notification";
import { useAuth } from "../../navigation/Auth/ProvideAuth";
import { Redirect } from "react-router-dom";
import {
  getUserList,
  addUser,
  updateUser,
  deleteUser,
} from "../../services/userServices";

export const UserList = () => {
  const auth = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState("");
  const searchInput = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    userName: "",
    password: "",
    roleId: 2,
  });

  const getData = () => {
    getUserList()
      .then((res) => {
        const items = res?.items || (Array.isArray(res) ? res : []);
        setUsers(items);
        setFilteredUsers(items);
      })
      .catch((err) => {
        const message = err?.data?.message || "Kullanıcılar yüklenirken bir hata oluştu.";
        notification.error({ message });
      });
  };

  useEffect(() => {
    if (auth.isAdmin) {
      getData();
    }
  }, [auth.isAdmin]);

  if (!auth.isAdmin) {
    return <Redirect to="/" />;
  }

  const onClose = () => {
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      userName: "",
      password: "",
      roleId: 2,
    });
    setEditId("");
  };

  const saveForm = (e) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.userName.trim()) {
      notification.error({ message: "Lütfen ad soyad ve e-posta alanlarını doldurunuz." });
      return;
    }

    if (!editId && (!formData.password || !formData.password.trim())) {
      notification.error({ message: "Yeni kullanıcı için şifre girilmesi zorunludur." });
      return;
    }

    setIsSubmitting(true);
    const payload = {
      fullName: formData.fullName.trim(),
      userName: formData.userName.trim(),
      email: formData.userName.trim(),
      roleId: Number(formData.roleId),
    };

    if (formData.password && formData.password.trim()) {
      payload.password = formData.password.trim();
    }

    if (editId) {
      payload.id = editId;
      payload.uniqueKey = editId;
      updateUser(payload)
        .then(() => {
          notification.success({ message: "Kullanıcı başarıyla güncellendi." });
          onClose();
          getData();
        })
        .catch((err) => {
          const message = err?.data?.message || "Kullanıcı güncellenirken bir hata oluştu.";
          notification.error({ message });
        })
        .finally(() => setIsSubmitting(false));
    } else {
      addUser(payload)
        .then(() => {
          notification.success({ message: "Kullanıcı başarıyla oluşturuldu." });
          onClose();
          getData();
        })
        .catch((err) => {
          const message = err?.data?.message || "Kullanıcı eklenirken bir hata oluştu.";
          notification.error({ message });
        })
        .finally(() => setIsSubmitting(false));
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    if (!value.trim()) {
      setFilteredUsers(users);
      return;
    }
    const val = value.toLocaleLowerCase("tr");
    const filtered = users.filter((u) => {
      const name = (u.fullName || "").toLocaleLowerCase("tr");
      const email = (u.userName || u.email || "").toLocaleLowerCase("tr");
      const role = (u.roleName || (u.roleId === 1 ? "admin" : "operasyon")).toLocaleLowerCase("tr");
      return name.includes(val) || email.includes(val) || role.includes(val);
    });
    setFilteredUsers(filtered);
  };

  const handleReset = () => {
    setSearchValue("");
    setFilteredUsers(users);
  };

  const onDeleteConfirm = (id) => {
    deleteUser(id)
      .then(() => {
        notification.success({ message: "Kullanıcı başarıyla silindi." });
        getData();
      })
      .catch((err) => {
        const message = err?.data?.message || "Kullanıcı silinirken bir hata oluştu.";
        notification.error({ message });
      });
  };

  const showEdit = (item) => {
    setFormData({
      fullName: item.fullName || "",
      userName: item.userName || item.email || "",
      password: "",
      roleId: item.roleId ? Number(item.roleId) : 2,
    });
    setEditId(item.id || item.uniqueKey);
    setOpen(true);
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Kullanıcı Yönetimi
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Sistem kullanıcılarını ve rollerini (Admin / Operasyon) yönetin.
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Yeni Kullanıcı Ekle
          </button>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Search bar */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <input
                  ref={searchInput}
                  type="text"
                  value={searchValue}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="İsim, e-posta veya rol ara..."
                  className="w-full px-3 py-2 pl-9 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                />
                <svg
                  className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {searchValue && (
                <button
                  onClick={handleReset}
                  className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  Temizle
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-800/60">
                <tr>
                  <th className="px-4 py-3">Kullanıcı</th>
                  <th className="px-4 py-3">E-Posta / Kullanıcı Adı</th>
                  <th className="px-4 py-3">Rol</th>
                  <th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                      Kayıtlı kullanıcı bulunamadı.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((item) => {
                    const itemId = item.id || item.uniqueKey;
                    const isSelf = String(itemId).toLowerCase() === String(auth.user).toLowerCase();
                    const isAdmin = item.roleId === 1 || item.roleName === "Admin";
                    return (
                      <tr key={itemId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-brand-500/10 dark:bg-brand-500/20 text-brand-600 dark:text-brand-400 flex items-center justify-center text-xs font-bold shrink-0">
                              {item.fullName?.[0]?.toUpperCase() || "U"}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {item.fullName}
                              {isSelf && (
                                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-normal">
                                  (Siz)
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                          {item.userName || item.email}
                        </td>
                        <td className="px-4 py-3">
                          {isAdmin ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                              Admin
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                              Operasyon
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => showEdit(item)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Düzenle
                            </button>
                            {!isSelf && (
                              <ConfirmButton
                                onConfirm={() => onDeleteConfirm(itemId)}
                                message="Kullanıcıyı silmek istediğinizden emin misiniz?"
                              >
                                <button className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Sil
                                </button>
                              </ConfirmButton>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Drawer / Modal */}
      {open && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]" onClick={onClose} />
          <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white dark:bg-gray-900 z-[9999] shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editId ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı Ekle"}
              </h3>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form onSubmit={saveForm} className="space-y-4">
                {/* FullName */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ad Soyad <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="Örn: Ahmet Yılmaz"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                  />
                </div>

                {/* UserName / Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    E-Posta (Kullanıcı Adı) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.userName}
                    onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                    placeholder="ornek@sirket.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kullanıcı Rolü <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.roleId}
                    onChange={(e) => setFormData({ ...formData, roleId: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                  >
                    <option value={1}>Admin (Tam Yetkili)</option>
                    <option value={2}>Operasyon (Kullanıcı Yönetimi Hariç)</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    * Kullanıcı yönetimi sayfasına yalnızca Admin rolündeki kullanıcılar erişebilir.
                  </p>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Şifre {editId ? "(Opsiyonel)" : <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    required={!editId}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editId ? "Değiştirmek istemiyorsanız boş bırakınız" : "••••••••"}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                  />
                  {editId && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Şifreyi değiştirmeyecekseniz bu alanı boş bırakabilirsiniz.
                    </p>
                  )}
                </div>

                {/* Submit button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <span>Kaydediliyor...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        <span>{editId ? "Güncelle" : "Kaydet"}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </>
  );
};
